import React from "react";

import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import StarIcon from "@mui/icons-material/Star";

import { PreviewImage } from "../../PreviewImage";

import { nonselect } from "../../../utils/basicFunctions";

import { getPreviewAssetId, getPreviewAssetSelectionId } from "./previewAssetUtils";

type PreviewAssetTabsProps = {
	version: any;

	previewAssets: any[];
	curPreviewAssetId: string;

	filterSelects: boolean;
	curFiltered: any[];
	selectedPreviewAssetIds: any[];

	onChange: (event: any, newValue: string) => void;
	onToggleSelected: (evt: any, previewAssetId: string, markSelected: boolean) => void;
};

function PreviewAssetTabsComponent({ version, previewAssets, curPreviewAssetId, filterSelects, curFiltered, selectedPreviewAssetIds, onChange, onToggleSelected }: PreviewAssetTabsProps) {
	const visiblePreviewAssets = React.useMemo(() => {
		return previewAssets.filter((previewAsset: any) => {
			const previewAssetId = getPreviewAssetId(previewAsset);
			const selectionId = getPreviewAssetSelectionId(previewAsset);

			return !filterSelects || previewAssetId === curPreviewAssetId || (curFiltered || []).indexOf(selectionId) > -1 || selectedPreviewAssetIds.indexOf(selectionId) > -1;
		});
	}, [previewAssets, curPreviewAssetId, filterSelects, curFiltered, selectedPreviewAssetIds]);

	const currentAssetExists = React.useMemo(() => {
		return previewAssets.some((previewAsset: any) => getPreviewAssetId(previewAsset) === curPreviewAssetId);
	}, [previewAssets, curPreviewAssetId]);

	const currentAssetIsVisible = React.useMemo(() => {
		return visiblePreviewAssets.some((previewAsset: any) => getPreviewAssetId(previewAsset) === curPreviewAssetId);
	}, [visiblePreviewAssets, curPreviewAssetId]);

	const tabsValue = currentAssetIsVisible ? curPreviewAssetId : false;

	const handleChange = React.useCallback(
		(event: any, newValue: string) => {
			if (!newValue || newValue === curPreviewAssetId) {
				return;
			}

			onChange(event, newValue);
		},
		[curPreviewAssetId, onChange]
	);

	if (!previewAssets.length) {
		return null;
	}

	if (!currentAssetExists && curPreviewAssetId.length) {
		return null;
	}

	return (
		<Tabs
			TabIndicatorProps={{
				children: (
					<span
						style={{
							height: "10px",
							width: "10px",
							backgroundColor: "#635ee7",
							borderRadius: "10px",
						}}
					/>
				),
			}}
			sx={{
				"& .MuiTabs-indicator": {
					height: "10px",
					display: "flex",
					justifyContent: "center",
					backgroundColor: "transparent",
				},
				width: "100%",
			}}
			value={tabsValue}
			onChange={handleChange}
			indicatorColor="primary"
			textColor="primary"
			scrollButtons={true}
			variant="scrollable"
		>
			{visiblePreviewAssets.map((previewAsset: any) => {
				const name = previewAsset?.file_name;
				const previewAssetId = getPreviewAssetId(previewAsset);
				const selectionId = getPreviewAssetSelectionId(previewAsset);
				const isSelected = selectedPreviewAssetIds.indexOf(selectionId) > -1;

				return (
					<Tab
						key={previewAssetId + "_vidPlayer"}
						value={previewAssetId}
						sx={{ width: 140 }}
						disableRipple
						label={
							<Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
								<Box sx={{ opacity: 0.85, right: "5px", mt: 0, top: "12px", position: "absolute", zIndex: 10000, pointerEvents: "none" }}>
									<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
										<IconButton
											component="div"
											sx={{ ml: 1, pointerEvents: "auto" }}
											aria-label="select"
											size="small"
											onClick={(evt) => {
												onToggleSelected(evt, previewAssetId, !isSelected);
											}}
										>
											{isSelected ? <StarIcon color={"primary"} fontSize="inherit" /> : <StarIcon sx={{ opacity: 0.25 }} fontSize="inherit" />}
										</IconButton>
									</Box>
								</Box>

								<PreviewImage
									key={previewAssetId + "_vidPlayer_img"}
									status={previewAsset?.status}
									version={version}
									assetId={previewAssetId}
									autoplay={false}
									is_visible={true}
									show_favorite={true}
									is_round={true}
									is_selected={previewAssetId === curPreviewAssetId}
									img_type={"thumb_scrub"}
									strict_asset_id={true}
									sx={{ width: 130, height: 73 }}
								/>

								<Tooltip placement="bottom" sx={{ ...nonselect, zIndex: "10000000!important" }} title={name}>
									<Box fontSize={"0.7em"} sx={{ ...nonselect, mt: 1, pb: 1, width: 120, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pt: "2px", opacity: 0.5 }}>
										{name}
									</Box>
								</Tooltip>
							</Box>
						}
					/>
				);
			})}
		</Tabs>
	);
}

export const PreviewAssetTabs = React.memo(PreviewAssetTabsComponent);
